import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import useIsInViewport from "use-is-in-viewport";

import {
  handleGoogleDriveImageUrl,
  handleGoogleDriveVideoUrl,
} from "../../../shared/utils";
import { MediaType } from "../../widget.types";

export function QuestionMedia({
  isActive,
  videoUrl,
  imageUrl,
  logoUrl,
}: MediaType & { isActive: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isInViewport, wrappedTargetRef] = useIsInViewport({
    target: (videoRef as unknown) as MutableRefObject<HTMLVideoElement>,
  });

  const autoPlayVideo = useCallback(() => {
    videoRef.current?.play().catch(() => {
      console.error(`Error playing video: ${videoUrl}`);
    });
  }, [videoUrl]);

  useEffect(() => {
    if (videoUrl) {
      if (isInViewport && isActive) {
        autoPlayVideo();
      } else if (!videoRef.current?.paused) {
        videoRef.current?.pause();
      }
    }
  }, [isActive, isInViewport, videoUrl, autoPlayVideo]);

  const mediaElement = useMemo(() => {
    if (videoUrl && imageUrl) {
      return (
        <video
          muted
          playsInline
          loop
          ref={wrappedTargetRef}
          controls={isActive}
          className="quiz--single-page__body__question__media__video"
          poster={handleGoogleDriveImageUrl(imageUrl)}
          data-testid="quiz-question-media-video"
        >
          <source src={handleGoogleDriveVideoUrl(videoUrl)} />
        </video>
      );
    }
    if (videoUrl) {
      return (
        <video
          muted
          playsInline
          loop
          ref={wrappedTargetRef}
          controls={isActive}
          className="quiz--single-page__body__question__media__video"
          data-testid="quiz-question-media-video"
        >
          <source src={handleGoogleDriveVideoUrl(videoUrl)} />
        </video>
      );
    }
    if (imageUrl) {
      return (
        <img
          src={handleGoogleDriveImageUrl(imageUrl)}
          alt="Question media"
          className="quiz-question-media-image"
          data-testid="quiz-question-media-image"
        />
      );
    }
    return <div />;
  }, [imageUrl, isActive, videoUrl, wrappedTargetRef]);

  return (
    <div className="quiz--single-page__body__question__media">
      <div className="quiz--single-page__body__question__media__sponsor">
        <img
          className="quiz--single-page__body__question__media__sponsor__image"
          data-testid="quiz-question-media-sponsor"
          src={handleGoogleDriveImageUrl(logoUrl)}
          alt="Brand logo"
        />
      </div>
      {mediaElement}
    </div>
  );
}
